<template>
  <div class="column-inner">

    <!-- BitlyFool ActivityPub ↔ Nostr Bridge -->
    <section class="bitlyfool-bridge-panel">
      <div class="bitlyfool-bridge-header">
        <div class="bitlyfool-bridge-eyebrow">
          BITLYFOOL DECENTRALIZED SOCIAL INFRASTRUCTURE
        </div>

        <h1>THE BRIDGE BETWEEN<br>ACTIVITYPUB &amp; NOSTR</h1>

        <p class="bitlyfool-bridge-lead">
          The social web doesn't have to live on one network.
        </p>

        <p class="bitlyfool-bridge-description">
          BitlyFool.us is an independent ActivityPub social network
          connected to the wider decentralized social web through
          BitlyFool's bridge and Nostr infrastructure.
        </p>
      </div>

      <div class="bitlyfool-network-diagram">

        <div class="bitlyfool-network-node">
          <div class="bitlyfool-node-dot"></div>
          <div>
            <strong>ACTIVITYPUB</strong>
            <span>FEDERATED SOCIAL NETWORK</span>
          </div>
        </div>

        <div class="bitlyfool-bridge-line">
          <span>BITLYFOOL</span>
        </div>

        <div class="bitlyfool-network-node bitlyfool-network-right">
          <div class="bitlyfool-node-dot"></div>
          <div>
            <strong>NOSTR</strong>
            <span>DECENTRALIZED RELAYS</span>
          </div>
        </div>

      </div>

      <div class="bitlyfool-bridge-cards">

        <div class="bitlyfool-bridge-card">
          <div class="bitlyfool-card-label">ACTIVITYPUB</div>
          <h2>Your BitlyFool home</h2>
          <p>
            BitlyFool.us provides an ActivityPub home on the federated
            social web.
          </p>

          <div class="bitlyfool-card-code">
            @username@bitlyfool.us
          </div>

          <router-link class="bitlyfool-card-link" to="/main/all">
            ENTER BITLYFOOL →
          </router-link>
        </div>

        <div class="bitlyfool-bridge-card">
          <div class="bitlyfool-card-label">BITLYFOOL BRIDGE</div>
          <h2>Connecting open networks</h2>
          <p>
            BitlyFool provides infrastructure connecting the ActivityPub
            and Nostr sides of the open social web.
          </p>

          <a
            class="bitlyfool-card-link"
            href="https://bridge.bitlyfool.us"
            target="_blank"
            rel="noopener noreferrer"
          >
            OPEN THE BRIDGE →
          </a>
        </div>

        <div class="bitlyfool-bridge-card">
          <div class="bitlyfool-card-label">NOSTR</div>
          <h2>BitlyFool Nostr relay</h2>
          <p>
            Connect compatible Nostr clients to BitlyFool's independent
            Nostr relay infrastructure.
          </p>

          <div class="bitlyfool-card-code">
            wss://relay.bitlyfool.net
          </div>

          <a
            class="bitlyfool-card-link"
            href="https://relay.bitlyfool.net"
            target="_blank"
            rel="noopener noreferrer"
          >
            VIEW RELAY →
          </a>
        </div>

      </div>

      <div class="bitlyfool-bridge-footer">
        <strong>THE OPEN SOCIAL WEB</strong>
        <span>
          ActivityPub • Nostr • Independent Infrastructure
        </span>
      </div>
    </section>

    <!-- Existing BitlyFool About panels -->
    <instance-specific-panel v-if="showInstanceSpecificPanel" />
    <staff-panel />
    <terms-of-service-panel />
    <LocalBubblePanel v-if="showLocalBubblePanel" />
    <MRFTransparencyPanel />
    <features-panel v-if="showFeaturesPanel" />

  </div>
</template>

<script src="./about.js"></script>

<style lang="scss">

.bitlyfool-bridge-panel {
  margin-bottom: 1rem;
  padding: 2.25rem;
  border-radius: 8px;
  background:
    radial-gradient(
      circle at 50% 0%,
      rgba(255, 166, 0, 0.10),
      transparent 48%
    ),
    rgba(15, 20, 28, 0.96);
  border: 1px solid rgba(255, 166, 0, 0.22);
  box-shadow:
    0 10px 35px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.025);
}

.bitlyfool-bridge-header {
  text-align: center;
  max-width: 850px;
  margin: 0 auto;
}

.bitlyfool-bridge-eyebrow {
  color: #f5a623;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  margin-bottom: 0.85rem;
}

.bitlyfool-bridge-header h1 {
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.8rem, 4vw, 3.2rem);
  line-height: 1.05;
  letter-spacing: -0.035em;
  font-weight: 800;
}

.bitlyfool-bridge-lead {
  margin: 1.25rem 0 0.45rem;
  color: #f5a623;
  font-size: 1.2rem;
  font-weight: 700;
}

.bitlyfool-bridge-description {
  max-width: 680px;
  margin: 0 auto;
  color: #aeb7c4;
  line-height: 1.65;
}

.bitlyfool-network-diagram {
  display: grid;
  grid-template-columns: 1fr minmax(130px, 220px) 1fr;
  align-items: center;
  gap: 1.25rem;
  margin: 2.25rem 0;
}

.bitlyfool-network-node {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.bitlyfool-network-right {
  justify-content: flex-end;
  text-align: right;
}

.bitlyfool-node-dot {
  width: 13px;
  height: 13px;
  flex: 0 0 13px;
  border: 2px solid #f5a623;
  border-radius: 50%;
  box-shadow: 0 0 16px rgba(245, 166, 35, 0.45);
}

.bitlyfool-network-node strong {
  display: block;
  color: #ffffff;
  font-size: 0.9rem;
  letter-spacing: 0.12em;
}

.bitlyfool-network-node span {
  display: block;
  margin-top: 0.15rem;
  color: #7f8b9a;
  font-size: 0.65rem;
  letter-spacing: 0.1em;
}

.bitlyfool-bridge-line {
  position: relative;
  text-align: center;
  color: #f5a623;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.15em;
}

.bitlyfool-bridge-line::before {
  content: "";
  display: block;
  height: 1px;
  margin-bottom: 0.55rem;
  background: linear-gradient(
    90deg,
    transparent,
    #f5a623,
    transparent
  );
  box-shadow: 0 0 12px rgba(245, 166, 35, 0.45);
}

.bitlyfool-bridge-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.bitlyfool-bridge-card {
  min-height: 245px;
  padding: 1.4rem;
  background: rgba(9, 13, 19, 0.9);
}

.bitlyfool-card-label {
  color: #f5a623;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.16em;
}

.bitlyfool-bridge-card h2 {
  margin: 0.55rem 0 0.6rem;
  color: #ffffff;
  font-size: 1.15rem;
}

.bitlyfool-bridge-card p {
  min-height: 70px;
  margin: 0;
  color: #9ba6b4;
  font-size: 0.88rem;
  line-height: 1.55;
}

.bitlyfool-card-code {
  margin: 0.8rem 0;
  padding: 0.55rem 0.7rem;
  overflow-wrap: anywhere;
  color: #dce3eb;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.75rem;
}

.bitlyfool-card-link {
  display: inline-block;
  margin-top: 0.65rem;
  color: #f5a623;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-decoration: none;
}

.bitlyfool-card-link:hover {
  color: #ffc15c;
}

.bitlyfool-bridge-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  color: #758191;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
}

.bitlyfool-bridge-footer strong {
  color: #f5a623;
}

@media (max-width: 760px) {
  .bitlyfool-bridge-panel {
    padding: 1.25rem;
  }

  .bitlyfool-network-diagram {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .bitlyfool-network-right {
    justify-content: flex-start;
    text-align: left;
  }

  .bitlyfool-bridge-line {
    order: 2;
  }

  .bitlyfool-network-right {
    order: 3;
  }

  .bitlyfool-bridge-cards {
    grid-template-columns: 1fr;
  }

  .bitlyfool-bridge-card p {
    min-height: auto;
  }
}

</style>
